import {
  FormEvent,
  FormStatus,
  Reservation,
  ReservationForms,
  bulkCancelPreviewQuery,
  bulkCancelService,
} from '@lib/state';
import React, { useEffect, useMemo } from 'react';
import { useObservable } from '../utils';
import { BulkCancelFormButtons } from './bulk-cancel-form-buttons';
import { ErrorDisplay, useFormEvents } from '../forms';
import { LoadingIndicator } from './loading-indicator';
import { BulkActivitiesDisplay } from './bulk-activities-display';
import { BulkActivityType, previewCancelItemsToReservationRows } from './bulk-change-utils';

interface Props {
  propertyId: string;
  corporateAccountId: string;
  selectedReservations: Reservation[];
  formEvents: FormEvent;
}

export const BulkCancelPanel: React.FC<Props> = ({
  propertyId,
  corporateAccountId,
  selectedReservations,
  formEvents,
}) => {
  const cancelPreviewItems = useObservable(bulkCancelPreviewQuery.bulkCancelPreviews);
  const [{ status, error }] = useFormEvents(ReservationForms.BulkCancelPreview);
  const errors = useMemo(() => {
    if (error) return error;
    return formEvents.error;
  }, [formEvents.error, error]);

  useEffect(() => {
    if (selectedReservations.length === 0 || !propertyId || !corporateAccountId) return;

    bulkCancelService.bulkCancelPreview(
      propertyId,
      corporateAccountId,
      selectedReservations.map(r => r.id)
    );
  }, [propertyId, corporateAccountId, selectedReservations.length]);

  const shouldDisabled = useMemo(() => {
    return (
      formEvents.status !== FormStatus.Initial ||
      selectedReservations.length === 0 ||
      status !== FormStatus.Success
    );
  }, [formEvents.status, selectedReservations.length, status]);

  return (
    <>
      <BulkCancelFormButtons
        propertyId={propertyId}
        corporateAccountId={corporateAccountId}
        bulkPreviewItems={cancelPreviewItems}
        disabled={shouldDisabled}
      />
      <ErrorDisplay error={errors} />
      {status === FormStatus.Pending && <LoadingIndicator loadingText="Validating..." />}
      {formEvents.status === FormStatus.Pending && (
        <LoadingIndicator loadingText="Applying cancellation..." />
      )}
      {!shouldDisabled && (
        <BulkActivitiesDisplay
          data={previewCancelItemsToReservationRows(cancelPreviewItems, selectedReservations)}
          bulkActivityType={BulkActivityType.Cancel}
        />
      )}
    </>
  );
};
